var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LoginStateScreen } from "healthex-common/device-hint.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { LoginSource } from "healthex-common/heap.dtos";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { ClearAuthState, SocialLoginAuthState } from "../Infra/Persistence";
import { isReauthLandingVisible } from "./deviceHintOTPReauth";
import { buildDeviceHintReauthOTPModel } from "./deviceHintReauthOTPModel";
import { NamedRoutes } from "./NamedRoutes";
import { determineOrgId, PatientConsentViewModel } from "./PatientConsentViewModel";
const t = getObservableT("patients");
export class PatientConsentLoginViewModel extends PatientConsentViewModel {
    constructor(container) {
        super(container);
        this.isSandboxLogin = false;
        this.loginStateRequested = false;
        this.loginUIState = "login";
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.loginUIManager = container.loginUIManager;
        this.routingUIManager = container.routingUIManager;
        this.persistence = container.persistence;
        this.clearAuthManager = container.clearAuthManager;
        this.socialLoginAuthManager = container.socialLoginAuthManager;
        this.deviceHintManager = container.deviceHintManager;
        this.deviceHintOTPManager = container.deviceHintOTPManager;
        this.isSandboxLogin = this.persistence.isSandboxLogin();
    }
    run() {
        this.loginStateRequested = true;
        void this.deviceHintManager.loadLoginState();
        this.disposer.reaction(() => this.deviceHintManager.loginState, (loginState) => {
            var _a;
            this.deviceHintOTPManager.pendingVerificationPhone = (_a = loginState === null || loginState === void 0 ? void 0 : loginState.maskedPhone) !== null && _a !== void 0 ? _a : null;
        }, { fireImmediately: true, name: "PatientConsentLoginViewModel_deviceHintLoginState" });
        this.disposer.reaction(() => this.clearAuthManager.state, () => {
            if (this.clearAuthManager.hasCompletedAuth) {
                this.handleClearLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientConsentLoginViewModel_clearAuthManagerState" });
        this.disposer.reaction(() => this.socialLoginAuthManager.state, () => {
            if (this.socialLoginAuthManager.hasCompletedAuth) {
                void this.handleSocialLoginResultIfNeeded();
            }
        }, { fireImmediately: true, name: "PatientConsentLoginViewModel_socialLoginAuthManagerState" });
        this.disposer.reaction(() => this.patientConsentUIManager.isPatientLoggedIn, () => this.handleDeviceHintOTPReauthLoginIfNeeded(), { name: "PatientConsentLoginViewModel_deviceHintOTPReauthLoggedIn" });
    }
    stop() {
        this.deviceHintOTPManager.resetVerificationFlow();
        this.disposer.dispose();
    }
    get isAccountLinkingError() {
        return this.socialLoginAuthManager.isAccountLinkingError;
    }
    get loginTitle() {
        return t("Log In to HealthEx");
    }
    get loginSupportingText() {
        return this.isHealthWalletEnabled
            ? t("Your HealthEx Wallet keeps your records secure and lets you control who can access them.")
            : t("Your HealthEx account keeps your records secure and lets you control who can access them.");
    }
    checkIfLoginNeeded(sandboxMode) {
        if (this.isPreview) {
            this.goToNextPage();
            return false;
        }
        if (this.isLoginInProgress)
            return false;
        if (sandboxMode === true || sandboxMode === false) {
            this.persistence.setSandboxLogin(sandboxMode);
        }
        if (this.isPatientLoggedIn) {
            if (!this.patientConsentUIManager.enteredViaResume) {
                this.goToNextPage();
            }
            return false;
        }
        if (this.isScreenshotMode)
            return false;
        this.patientConsentUIManager.setHasLoggedInDuringConsent();
        return true;
    }
    verifyWithClearIfNeeded(sandboxMode) {
        this.patientConsentUIManager.setSelectedLoginType("CLEAR");
        if (this.patientConsentUIManager.showEmailCollectionPage) {
            this.persistence.setSandboxLogin(sandboxMode);
            this.goToNextPage();
            return;
        }
        if (!this.checkIfLoginNeeded(sandboxMode))
            return;
        const useFallback = this.isOnlyClearLoginAllowed && this.loginUIState === "verifyingWithClearError";
        void this.clearLogin(useFallback, sandboxMode);
    }
    async continueWithGoogle() {
        this.patientConsentUIManager.setSelectedLoginType("SOCIAL");
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLogin(GFIDProvider.GOOGLE);
    }
    async continueWithMicrosoft() {
        this.patientConsentUIManager.setSelectedLoginType("SOCIAL");
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLogin(GFIDProvider.MICROSOFT);
    }
    async continueWithApple() {
        this.patientConsentUIManager.setSelectedLoginType("SOCIAL");
        if (!this.checkIfLoginNeeded())
            return;
        await this.startSocialLogin(GFIDProvider.APPLE);
    }
    get isActuallyPatientLoggedIn() {
        return this.patientConsentUIManager.isPatientLoggedIn;
    }
    get isPatientLoggedIn() {
        if (this.isScreenshotMode)
            return false;
        return this.patientConsentUIManager.isPatientLoggedIn;
    }
    get clearButtonLabel() {
        return this.isPatientLoggedIn ? t("Continue") : t("Continue with CLEAR");
    }
    get googleButtonLabel() {
        if (this.isGoogleError) {
            return t("Try again with Google");
        }
        return t("Continue with Google");
    }
    get microsoftButtonLabel() {
        if (this.isMicrosoftError) {
            return t("Try again with Microsoft");
        }
        return t("Continue with Microsoft");
    }
    get appleButtonLabel() {
        if (this.isAppleError) {
            return t("Try again with Apple");
        }
        return t("Continue with Apple");
    }
    get tryAgainButtonLabel() {
        if (this.isClearError) {
            return t("Try again with CLEAR");
        }
        return t("Continue with CLEAR");
    }
    get flipLongPressLoginButton() {
        return this.isSandboxLogin;
    }
    get isClearLoggingIn() {
        return this.clearAuthManager.state === ClearAuthState.IN_PROGRESS;
    }
    get requestedGFIDProvider() {
        return this.socialLoginAuthManager.requestedGFIDProvider;
    }
    get responseGFIDProvider() {
        return this.socialLoginAuthManager.responseGFIDProvider;
    }
    get isGoogleLoggingIn() {
        return this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.GOOGLE;
    }
    get isMicrosoftLoggingIn() {
        return (this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.MICROSOFT);
    }
    get isAppleLoggingIn() {
        return this.socialLoginAuthManager.state === SocialLoginAuthState.IN_PROGRESS && this.requestedGFIDProvider === GFIDProvider.APPLE;
    }
    get isLoginInProgress() {
        return this.isClearLoggingIn || this.isGoogleLoggingIn || this.isMicrosoftLoggingIn || this.isAppleLoggingIn;
    }
    get isClearError() {
        return this.loginUIState === "verifyingWithClearError";
    }
    get isGoogleError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.GOOGLE;
    }
    get isMicrosoftError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.MICROSOFT;
    }
    get isAppleError() {
        return this.loginUIState === "verifyingWithSocialLoginError" && this.responseGFIDProvider === GFIDProvider.APPLE;
    }
    get isSocialLoginError() {
        return this.loginUIState === "verifyingWithSocialLoginError";
    }
    get isLoginError() {
        return this.isClearError || this.isSocialLoginError;
    }
    get isOnlyClearLoginAllowed() {
        return this.patientConsentUIManager.isOnlyClearLoginAllowed;
    }
    get loginStateLoaded() {
        return this.deviceHintManager.loginStateLoaded;
    }
    get isLoadingLoginState() {
        return !this.loginStateRequested || this.deviceHintManager.loadingLoginState;
    }
    get loginScreen() {
        var _a, _b;
        return (_b = (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.screen) !== null && _b !== void 0 ? _b : LoginStateScreen.STANDARD_LOGIN;
    }
    get reauthFirstName() {
        var _a;
        return (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.firstName;
    }
    get showReauthLanding() {
        return isReauthLandingVisible({
            loginScreen: this.loginScreen,
            reauthFirstName: this.reauthFirstName,
            isLoggedIn: this.isActuallyPatientLoggedIn,
        });
    }
    get reauthProvider() {
        var _a;
        return (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.provider;
    }
    get isForgettingDevice() {
        return this.deviceHintManager.forgettingDevice;
    }
    async forgetDevice() {
        this.deviceHintOTPManager.resetVerificationFlow();
        await this.deviceHintManager.forgetDevice();
    }
    startDeviceHintOTP() {
        var _a, _b;
        if (!this.checkIfLoginNeeded())
            return false;
        this.deviceHintOTPManager.pendingVerificationPhone = (_b = (_a = this.deviceHintManager.loginState) === null || _a === void 0 ? void 0 : _a.maskedPhone) !== null && _b !== void 0 ? _b : null;
        void this.deviceHintOTPManager.sendVerificationCode(true, LoginSource.CONSENT_LOGIN);
        return true;
    }
    resetDeviceHintOTP() {
        this.deviceHintOTPManager.resetVerificationFlow();
    }
    get reauthOTPModel() {
        return buildDeviceHintReauthOTPModel({
            deviceHintOTPManager: this.deviceHintOTPManager,
            isSandboxLogin: this.persistence.isSandboxLogin(),
            source: LoginSource.CONSENT_LOGIN,
            verifyWithClear: (sandboxMode = false) => {
                this.persistence.setSandboxLogin(sandboxMode);
                void this.verifyWithClearIfNeeded(sandboxMode);
            },
        });
    }
    get isAppleLoginButtonEnabled() {
        return this.patientConsentUIManager.isAppleLoginButtonEnabled;
    }
    async clearLogin(useFallback, sandboxMode) {
        await this.patientConsentUIManager.saveConsentFlowProgressByEmail({ clearLoginAttempted: true });
        this.patientConsentUIManager.saveConsentSession();
        await this.clearAuthManager.redirectToClearLoginPage(window.location.hash, LoginSource.CONSENT_LOGIN, determineOrgId(this.loginUIManager, this.studyInfo), useFallback, sandboxMode, this.patientConsentUIManager.patientCollectedEmail);
    }
    async startSocialLogin(gfidProvider) {
        this.patientConsentUIManager.saveConsentSession();
        await this.socialLoginAuthManager.redirectToSocialLoginPage(window.location.hash, gfidProvider, LoginSource.CONSENT_LOGIN, determineOrgId(this.loginUIManager, this.studyInfo));
    }
    loginResultConfig() {
        var _a, _b, _c, _d;
        const currentRouteName = (_b = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== null && _b !== void 0 ? _b : "";
        const isValidRoute = [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName,
            NamedRoutes.PATIENT_IAS_CONSENT.routeName,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName,
        ].includes(currentRouteName);
        const isValidStep = ((_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.step) === "login" ||
            ((_d = this.routingUIManager.currentRouteParams) === null || _d === void 0 ? void 0 : _d.step) === "email-collection";
        return { isValidRoute, isValidStep };
    }
    handleDeviceHintOTPReauthLoginIfNeeded() {
        if (this.patientConsentUIManager.isPatientLoggedIn &&
            this.loginUIState === "login" &&
            !this.clearAuthManager.hasCompletedAuth &&
            !this.socialLoginAuthManager.hasCompletedAuth &&
            !this.isPreview &&
            !this.patientConsentUIManager.enteredViaResume) {
            this.loginUIState = "completingLogin";
            this.patientConsentUIManager.setPostLoginAction(() => {
                this.goToNextPage();
            });
        }
    }
    handleClearLoginResultIfNeeded() {
        const { isValidRoute, isValidStep } = this.loginResultConfig();
        const clearAuthData = this.clearAuthManager.getClearAuthData();
        if (!!clearAuthData && isValidRoute && isValidStep) {
            this.patientConsentUIManager.restoreConsentSession();
            if (this.clearAuthManager.state === ClearAuthState.EXITED) {
                this.clearAuthManager.resetAuth();
                return;
            }
            this.loginUIState = "completingLogin";
            if (this.patientConsentUIManager.isPatientLoggedIn) {
                this.patientConsentUIManager.setPostLoginAction(() => {
                    this.goToNextPage();
                });
            }
            else {
                this.loginUIState = "verifyingWithClearError";
            }
            this.clearAuthManager.resetAuth();
        }
    }
    async handleSocialLoginResultIfNeeded() {
        const { isValidRoute, isValidStep } = this.loginResultConfig();
        const socialLoginAuthData = this.socialLoginAuthManager.getSocialLoginAuthData();
        if (!!socialLoginAuthData && isValidRoute && isValidStep) {
            this.patientConsentUIManager.restoreConsentSession();
            if (this.socialLoginAuthManager.hasPendingSocialLogin) {
                if (this.socialLoginAuthManager.shouldShowAccountSetupScreen === false) {
                    this.loginUIState = "completingLogin";
                    this.socialLoginAuthManager.setSocialLoginAuthData(null);
                    await this.completeVerification();
                    this.socialLoginAuthManager.resetAuth();
                    return;
                }
                this.loginUIState = "completingLogin";
                this.socialLoginAuthManager.setSocialLoginAuthData(null);
                if (!(this.patientConsentUIManager.enteredViaResume && this.patientConsentUIManager.isPatientLoggedIn)) {
                    this.goToNextPage();
                }
                return;
            }
            this.loginUIState = "completingLogin";
            if (this.patientConsentUIManager.isPatientLoggedIn) {
                this.patientConsentUIManager.setPostLoginAction(() => {
                    this.goToNextPage();
                });
            }
            else if (this.socialLoginAuthManager.isAccountLinkingError) {
                this.loginUIState = "accountLinkingError";
            }
            else {
                this.loginUIState = "verifyingWithSocialLoginError";
            }
            this.socialLoginAuthManager.resetAuth();
        }
    }
    async completeVerification() {
        var _a, _b, _c, _d;
        await this.authManager.waitForPendingRefresh();
        const pendingData = this.socialLoginAuthManager.pendingSocialLoginData;
        if (!pendingData)
            return;
        const result = await this.socialLoginAuthManager.completeVerificationFlow({
            firstName: (_a = pendingData.firstName) !== null && _a !== void 0 ? _a : "",
            lastName: (_b = pendingData.lastName) !== null && _b !== void 0 ? _b : "",
            dateOfBirth: (_c = pendingData.dateOfBirth) !== null && _c !== void 0 ? _c : "",
            phone: (_d = pendingData.phoneNumber) !== null && _d !== void 0 ? _d : "",
        });
        if (result.ok) {
            this.patientConsentUIManager.setPostLoginAction(() => {
                this.goToNextPage();
            });
        }
    }
}
__decorate([
    observable
], PatientConsentLoginViewModel.prototype, "isSandboxLogin", void 0);
__decorate([
    observable
], PatientConsentLoginViewModel.prototype, "loginStateRequested", void 0);
__decorate([
    observable
], PatientConsentLoginViewModel.prototype, "loginUIState", void 0);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isAccountLinkingError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "loginTitle", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "loginSupportingText", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isActuallyPatientLoggedIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isPatientLoggedIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "clearButtonLabel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "googleButtonLabel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "microsoftButtonLabel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "appleButtonLabel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "tryAgainButtonLabel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "flipLongPressLoginButton", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isClearLoggingIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "requestedGFIDProvider", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "responseGFIDProvider", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isGoogleLoggingIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isMicrosoftLoggingIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isAppleLoggingIn", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isLoginInProgress", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isClearError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isGoogleError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isMicrosoftError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isAppleError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isSocialLoginError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isLoginError", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isOnlyClearLoginAllowed", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "loginStateLoaded", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isLoadingLoginState", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "loginScreen", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "reauthFirstName", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "showReauthLanding", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "reauthProvider", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isForgettingDevice", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "reauthOTPModel", null);
__decorate([
    computed
], PatientConsentLoginViewModel.prototype, "isAppleLoginButtonEnabled", null);
